import { useFetchPageV2 } from "~/core/composables/useFetchPageV2";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.interface";
import operationManagerService from "~/core/services/operationManager.service";
import type { FiltersSelection } from "~/types";
import {
  Filters,
  FiltersType,
  OPERATOR_RSQL_ARRAY_CONTAINS,
  OPERATOR_RSQL_EQUAL,
  OPERATOR_RSQL_IN,
} from "~/types";

export function useDtwinOperations({
  searchMode,
  useQueryParametersForPagination,
  defaultSort,
  organizationCode,
}: UseFetchParameters) {
  const service = operationManagerService.getOperations.bind(
    operationManagerService,
  );

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.DTWIN_OPERATION_UID,
      { key: "deviceTwinUid", operator: OPERATOR_RSQL_EQUAL, value: [] },
    ],
    [
      Filters.DTWIN_OPERATION_TYPE,
      { key: "type", operator: OPERATOR_RSQL_IN, value: [] },
    ],
    [
      Filters.DTWIN_OPERATION_SERVICE_ORIGIN,
      { key: "serviceOrigin", operator: OPERATOR_RSQL_EQUAL, value: [] },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  sort.value = defaultSort;
  const { isLoading, results, error, fetchData } = useFetchPageV2({
    service,
    searchParams,
    sort,
    useQueryParametersForPagination,
    organizationCode,
    sortOverrideCallback: search,
  });

  return {
    error,
    fetchData,
    filters,
    isLoading,
    onSearch,
    onSortChange,
    results,
    search,
    setFilter,
    sort,
  };
}

export function useDtwinOperationModels({
  organizationCode,
  searchMode,
  useQueryParametersForPagination,
}: UseFetchParameters) {
  const service = operationManagerService.getModels.bind(
    operationManagerService,
  );

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.DTWIN_OPERATION_MODEL_TYPE,
      { key: "type", operator: OPERATOR_RSQL_EQUAL, value: "" },
    ],
    [
      Filters.DTWIN_OPERATION_MODEL_DEVICE_TYPE,
      { key: "deviceTypes", operator: OPERATOR_RSQL_ARRAY_CONTAINS, value: [] },
    ],
    [
      Filters.DTWIN_OPERATION_MODEL_HIDDEN,
      {
        key: "hidden",
        operator: OPERATOR_RSQL_EQUAL,
        value: "",
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData, fetchAllData, allResults } =
    useFetchPageV2({
      organizationCode,
      service,
      searchParams,
      sort,
      useQueryParametersForPagination,
    });

  return {
    error,
    fetchData,
    fetchAllData,
    filters,
    isLoading,
    onSearch,
    onSortChange,
    results,
    allResults,
    search,
    setFilter,
    sort,
  };
}
